import type { BenefitWithNullablePlansArray, PlanOrNullArray } from './types'
import type { ComparisonTableFragment } from '@graphql/fragments/ComparisonTableFragment.graphql'

export const getTableHeaderRows = (
    plans: ComparisonTableFragment['plans'],
): PlanOrNullArray => {
    // if there are no plans, return an empty array
    if (!plans?.items?.length) return []

    // create an array to hold the rows
    const rows: PlanOrNullArray = []

    // iterate through the plans
    for (let i = 0; i < plans.items.length; i++) {
        // add the plan to the row
        rows.push(plans.items[i])

        // put a spacer in between each plan but not at the end
        if (plans.items[i + 1]) rows.push(null)
    }

    return rows
}

export const getTableBodyRows = (
    benefits: ComparisonTableFragment['benefits'],
    plans: ComparisonTableFragment['plans'],
): BenefitWithNullablePlansArray[] => {
    // if there are no benefits or plans, return an empty array
    if (!benefits?.items?.length || !plans?.items?.length) return []

    // create an array to hold the rows
    const rows: BenefitWithNullablePlansArray[] = []

    // iterate through the benefits
    for (let i = 0; i < benefits?.items.length; i++) {
        // create an array to hold the cells for the current row, starting with the benefit and a spacer
        const row: BenefitWithNullablePlansArray = [
            {
                type: 'benefit',
                benefit: benefits.items[i],
                isLast: false,
            },
            null,
        ]

        // iterate through the plans
        for (let j = 0; j < plans.items.length; j++) {
            // get the current plan
            const plan = plans.items[j]

            // get the current benefit
            const benefit = benefits.items[i]

            // get the plan benefit that matches the current benefit
            const planBenefit = plan?.benefits?.items.find(
                (planBenefit) => planBenefit?.benefit?.key === benefit.key,
            )

            // add the plan benefit to the row
            row.push({
                type: 'planBenefit',
                value: planBenefit,
                isEmpty: !planBenefit,
                theme: plan?.theme,
                isLast: false,
            })

            // put a spacer in between each plan but not at the end
            if (plans.items[j + 1]) row.push(null)
        }

        rows.push(row)
    }

    // iterate over all the rows and set isLast to true on the last cell for each plan
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i]

        for (let j = 0; j < row.length; j++) {
            const cell = row[j]

            if (cell?.type !== 'planBenefit') continue

            const nextRowSameCell = rows[i + 1]?.[j]

            if (nextRowSameCell?.isEmpty || i === rows.length - 1) {
                cell.isLast = true
            }
        }
    }

    return rows
}
